/**
 * Homepage hero slideshow.
 *
 * Crossfading slides with arrow/dot controls, swipe, keyboard, and optional
 * autoplay that pauses on hover / focus / hidden tab / off-screen and stops
 * once a visitor takes over with the arrows or dots. Background video inside
 * a slide is owned by component-banner-background-video.js; this file only
 * sequences slides and nudges the active slide's <video> to play.
 */
(function () {
  const SWIPE_THRESHOLD = 44;
  const SWIPE_RATIO = 1.4; // horizontal must beat vertical by this much

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class HomeHero {
    constructor(root) {
      this.root = root;
      this.slides = [...root.querySelectorAll('[data-hero-slide]')];
      this.dots = [...root.querySelectorAll('[data-hero-dot]')];
      this.prevBtn = root.querySelector('[data-hero-prev]');
      this.nextBtn = root.querySelector('[data-hero-next]');
      this.live = root.querySelector('[data-hero-live]');

      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.count = this.slides.length;
      this.autoplayEnabled = root.dataset.autoplay === 'true' && this.count > 1 && !reducedMotion.matches;
      this.interval = Math.max(3000, parseInt(root.dataset.interval, 10) || 8000);
      this.timer = 0;
      this.leavingTimer = 0;
      this.fadeMs = this.readFadeMs();

      this.isHovered = false;
      this.isFocused = false;
      this.isVisible = true;
      this.userTookOver = false;

      this.pointer = null;

      this.onPrev = () => this.userGo(this.index - 1);
      this.onNext = () => this.userGo(this.index + 1);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onEnter = () => { this.isHovered = true; this.syncAutoplay(); };
      this.onLeave = () => { this.isHovered = false; this.syncAutoplay(); };
      this.onFocusIn = () => { this.isFocused = true; this.syncAutoplay(); };
      this.onFocusOut = (e) => {
        if (!this.root.contains(e.relatedTarget)) { this.isFocused = false; this.syncAutoplay(); }
      };
      this.onVisibility = () => this.syncAutoplay();
      this.onMotionChange = () => {
        if (reducedMotion.matches) { this.autoplayEnabled = false; }
        this.syncAutoplay();
      };

      this.init();
    }

    readFadeMs() {
      const raw = getComputedStyle(this.root).getPropertyValue('--home-hero-fade').trim();
      if (raw.endsWith('ms')) return parseFloat(raw) || 1000;
      if (raw.endsWith('s')) return (parseFloat(raw) || 1) * 1000;
      return 1000;
    }

    init() {
      if (this.count < 2) {
        this.playVideoIn(this.slides[0]);
        return;
      }

      if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);
      // One delegated, removable click handler for the dots instead of an
      // anonymous listener per dot (which destroy() could never remove) (D-28).
      this.onDotClick = (event) => {
        const dot = event.target.closest('[data-hero-dot]');
        if (!dot || !this.root.contains(dot)) return;
        const i = this.dots.indexOf(dot);
        if (i >= 0) this.userGo(i);
      };
      this.root.addEventListener('click', this.onDotClick);

      this.root.addEventListener('keydown', this.onKeydown);
      this.root.addEventListener('pointerdown', this.onPointerDown);
      this.root.addEventListener('pointermove', this.onPointerMove);
      this.root.addEventListener('pointerup', this.onPointerUp);
      this.root.addEventListener('pointercancel', this.onPointerUp);

      this.root.addEventListener('mouseenter', this.onEnter);
      this.root.addEventListener('mouseleave', this.onLeave);
      this.root.addEventListener('focusin', this.onFocusIn);
      this.root.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('visibilitychange', this.onVisibility);
      reducedMotion.addEventListener('change', this.onMotionChange);

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            this.isVisible = entries.some((e) => e.isIntersecting);
            this.syncAutoplay();
          },
          { threshold: 0.25 }
        );
        this.io.observe(this.root);
      }

      this.applyState(this.index, null);
      this.playVideoIn(this.slides[this.index]);
      this.syncAutoplay();
    }

    /* ── Navigation ─────────────────────────────────────────── */

    userGo(i) {
      this.userTookOver = true;
      this.goTo(i);
      this.syncAutoplay();
    }

    goTo(i) {
      if (this.count < 2) return;
      const next = ((i % this.count) + this.count) % this.count;
      if (next === this.index) return;

      const from = this.slides[this.index];
      const to = this.slides[next];

      this.applyState(next, from);
      this.index = next;

      this.pauseVideoIn(from);
      this.playVideoIn(to);
      this.restartProgress();
    }

    applyState(nextIndex, leavingSlide) {
      this.slides.forEach((slide, i) => {
        const active = i === nextIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.querySelectorAll('a, button').forEach((el) => {
          if (active) el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', '-1');
        });
      });

      if (leavingSlide) {
        window.clearTimeout(this.leavingTimer);
        this.slides.forEach((s) => s.classList.remove('is-leaving'));
        leavingSlide.classList.add('is-leaving');
        this.leavingTimer = window.setTimeout(() => {
          leavingSlide.classList.remove('is-leaving');
        }, this.fadeMs + 40);
      }

      this.dots.forEach((dot, i) => {
        const active = i === nextIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      if (this.live) {
        const heading = this.slides[nextIndex].querySelector('.home-hero__heading');
        const label = heading ? heading.textContent.trim() : '';
        this.live.textContent = `Slide ${nextIndex + 1} of ${this.count}${label ? ': ' + label : ''}`;
      }
    }

    // The leaving slide is still on screen for the fade; freezing its video on
    // the current frame means the compositor blends one static layer instead
    // of a decoding 1080p stream against the incoming slide.
    pauseVideoIn(slide) {
      if (!slide) return;
      const video = slide.querySelector('video.banner__video');
      if (!video || video.paused) return;
      video.pause();
    }

    playVideoIn(slide) {
      if (!slide) return;
      const video = slide.querySelector('video.banner__video');
      if (!video || !video.src) return;
      if (reducedMotion.matches) return;
      if (document.documentElement.classList.contains('drawer-open')) return;
      try {
        if (navigator.audioSession) navigator.audioSession.type = 'ambient';
      } catch (error) {}
      video.muted = true;
      video.volume = 0;
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }

    /* ── Autoplay ───────────────────────────────────────────── */

    shouldRun() {
      return (
        this.autoplayEnabled &&
        !this.userTookOver &&
        !this.isHovered &&
        !this.isFocused &&
        this.isVisible &&
        !document.hidden
      );
    }

    syncAutoplay() {
      const running = this.autoplayEnabled && !this.userTookOver;
      this.root.classList.toggle('is-autoplaying', running);

      if (!running) {
        this.root.classList.remove('is-paused');
        this.stopTimer();
        return;
      }

      if (this.shouldRun()) {
        this.root.classList.remove('is-paused');
        this.startTimer();
      } else {
        this.root.classList.add('is-paused');
        this.stopTimer();
      }
    }

    startTimer() {
      if (this.timer) return;
      this.timer = window.setTimeout(() => {
        this.timer = 0;
        this.goTo(this.index + 1);
        if (this.shouldRun()) this.startTimer();
      }, this.interval);
    }

    stopTimer() {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = 0;
      }
    }

    // Restart the CSS progress animation on the newly active dot and realign
    // the timer so the fill and the slide change stay in step.
    restartProgress() {
      const fill = this.root.querySelector('.home-hero__dot.is-active .home-hero__dot-fill');
      if (fill) {
        fill.style.animation = 'none';
        // force reflow so the animation restarts from 0
        void fill.offsetWidth;
        fill.style.animation = '';
      }
      if (this.timer) {
        this.stopTimer();
        if (this.shouldRun()) this.startTimer();
      }
    }

    /* ── Input ──────────────────────────────────────────────── */

    onKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.userGo(this.index - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.userGo(this.index + 1);
      }
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      // Don't hijack taps on controls/links.
      if (event.target.closest('a, button')) return;
      this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, moved: false };
    }

    onPointerMove(event) {
      if (!this.pointer || event.pointerId !== this.pointer.id) return;
      const dx = event.clientX - this.pointer.x;
      const dy = event.clientY - this.pointer.y;
      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) this.pointer.moved = true;
    }

    onPointerUp(event) {
      if (!this.pointer || event.pointerId !== this.pointer.id) return;
      const dx = event.clientX - this.pointer.x;
      const dy = event.clientY - this.pointer.y;
      this.pointer = null;

      if (Math.abs(dx) < SWIPE_THRESHOLD) return;
      if (Math.abs(dx) < Math.abs(dy) * SWIPE_RATIO) return;

      this.userGo(dx < 0 ? this.index + 1 : this.index - 1);
    }

    /* ── Lifecycle ──────────────────────────────────────────── */

    destroy() {
      this.stopTimer();
      window.clearTimeout(this.leavingTimer);
      if (this.io) this.io.disconnect();

      if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNext);
      if (this.onDotClick) this.root.removeEventListener('click', this.onDotClick);
      this.root.removeEventListener('keydown', this.onKeydown);
      this.root.removeEventListener('pointerdown', this.onPointerDown);
      this.root.removeEventListener('pointermove', this.onPointerMove);
      this.root.removeEventListener('pointerup', this.onPointerUp);
      this.root.removeEventListener('pointercancel', this.onPointerUp);
      this.root.removeEventListener('mouseenter', this.onEnter);
      this.root.removeEventListener('mouseleave', this.onLeave);
      this.root.removeEventListener('focusin', this.onFocusIn);
      this.root.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('visibilitychange', this.onVisibility);
      reducedMotion.removeEventListener('change', this.onMotionChange);
    }
  }

  function init(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-home-hero]').forEach((el) => {
      if (el._homeHero) el._homeHero.destroy();
      el._homeHero = new HomeHero(el);
    });
  }

  function destroy(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-home-hero]').forEach((el) => {
      if (el._homeHero) {
        el._homeHero.destroy();
        delete el._homeHero;
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  // Theme editor hooks
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => destroy(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const slide = e.target && e.target.closest ? e.target.closest('[data-hero-slide]') : null;
    const root = slide ? slide.closest('[data-home-hero]') : null;
    if (!root || !root._homeHero) return;
    const i = root._homeHero.slides.indexOf(slide);
    if (i > -1) root._homeHero.userGo(i);
  });
})();
